<template>
  <transition name="fade">
      <el-row :gutter="10" class="mt-10" style="width: 100%">
        <el-col :span="24">

          <div class="card" v-if="!state.loaderEnabled" style="width: 100%;">
            <div class="card-header border-0 pt-6">
              <div class="card-title" style="width:100%;">
                <h2>Liste des tenants</h2>
              </div>
              <SearchAndFilter
                :listInitial="state.initialMyList"
                :filters="tableFilters"
                @returnSearch="state.myList = $event"
                @returnLoadingDatatable="state.loadingDatatable = $event"
              ></SearchAndFilter>
            </div>
            <div class="card-body pt-0">
              <div
                v-if="state.myList.length == 0"
                class="alert bg-light-primary alert-primary d-flex align-items-center p-5 mb-10"
              >
                <span class="svg-icon svg-icon-2hx svg-icon-primary me-4">
                  <inline-svg src="media/icons/duotune/general/gen048.svg" />
                </span>
                <div class="d-flex flex-column">
                  <h4 class="mb-1 text-dark">Aucune ligne à afficher</h4>
                  <span>Il n'existe aucun tenant correspondant à vos critères de recherche.</span>
                </div>
              </div>
              <Datatable
                v-if="state.myList && state.myList.length > 0"
                :table-data="state.myList"
                :table-header="tableHeader"
                :enable-items-per-page-dropdown="true"
                :rows-per-page="50"
                empty-table-text="Aucune ligne à afficher."
                :key="state.loadingDatatable"
                class="table-small-td"
              >
                <template v-slot:cell-code_ark="{ row: data }">
                  <span class="fw-bold">{{ data.code_ark }}</span>
                </template>
                <template v-slot:cell-name="{ row: data }">{{ data.name }}</template>
                <template v-slot:cell-db_address="{ row: data }">
                  <span class="text-gray-700 fs-7">{{ data.db_address }}:{{ data.db_port }}</span>
                </template>
                <template v-slot:cell-db_database="{ row: data }">{{ data.db_database }}</template>
                <template v-slot:cell-oidc_org_id="{ row: data }">
                  <span class="text-muted fs-7">{{ data.oidc_org_id || '-' }}</span>
                </template>
                <template v-slot:cell-active="{ row: data }">
                  <span :class="data.active == 1 ? 'badge badge-light-success' : 'badge badge-light-danger'">
                    {{ data.active == 1 ? 'Actif' : 'Inactif' }}
                  </span>
                </template>
                <template v-slot:cell-actions="{ row: data }">
                  <div style="width: 220px;">
                    <a class="btn btn-sm btn-custom-white mx-1" @click="displayTenant(data)">
                      <span class="svg-icon svg-icon-3 svg-icon-primary">
                        <inline-svg src="media/icons/duotune/art/art005.svg"/>
                      </span>
                      Modifier
                    </a>
                    <el-popconfirm
                      v-if="data.active == 1"
                      confirm-button-text="Oui, désactiver"
                      cancel-button-text="Non"
                      :title="`Désactiver le tenant '${data.name}' ?`"
                      @confirm="onDeactivate(data.code_ark)"
                    >
                      <template #reference>
                        <a class="btn btn-sm btn-custom-white mx-1">
                          <span class="svg-icon svg-icon-2 svg-icon-danger">
                            <inline-svg src="media/icons/duotune/general/gen047.svg"/>
                          </span>
                          Désactiver
                        </a>
                      </template>
                    </el-popconfirm>
                  </div>
                </template>
              </Datatable>
            </div>
          </div>

        </el-col>
      </el-row>
  </transition>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, reactive } from "vue";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import SearchAndFilter from "@/views/still/search_and_filters.vue";
import mAxiosApi from "@/api";
import { ElNotification } from 'element-plus'

export default defineComponent({
  name: "TenantsList",
  components: {
    Datatable,
    SearchAndFilter,
  },

  emits: ["displayTenant"],

  setup(props, { emit }) {
    const state = reactive({
      loaderEnabled: true,
      myList: [] as any,
      initialMyList: [],
      loadingDatatable: 0,
    });

    const tableHeader = ref([
      { name: "Code Ark", key: "code_ark", sortable: true },
      { name: "Nom", key: "name", sortable: true },
      { name: "Serveur DB", key: "db_address", sortable: false },
      { name: "Base de données", key: "db_database", sortable: false },
      { name: "OIDC Org ID", key: "oidc_org_id", sortable: false },
      { name: "Statut", key: "active", sortable: true },
      { name: "", key: "actions", sortable: false },
    ]);

    const tableFilters = ref([
      { name: "Statut", key: "active_label", label: "active_label" },
    ]);

    onMounted(async () => {
      try {
        const response = await mAxiosApi.get("/getTenants");
        const results = response.data.results || [];
        results.forEach((t: any) => {
          t.active_label = t.active == 1 ? "Actif" : "Inactif";
        });
        state.myList = results;
        state.initialMyList = results;
      } catch {
        state.myList = [];
        state.initialMyList = [];
      }
      state.loaderEnabled = false;
    });

    const displayTenant = (d: any) => {
      emit("displayTenant", d);
    };

    const onDeactivate = async (codeArk: string) => {
      try {
        await mAxiosApi.post("/deleteTenant", { code_ark: codeArk });
        ElNotification({ title: 'Succès', message: 'Tenant désactivé avec succès !', type: 'success' });
        const response = await mAxiosApi.get("/getTenants");
        const results = response.data.results || [];
        results.forEach((t: any) => {
          t.active_label = t.active == 1 ? "Actif" : "Inactif";
        });
        state.myList = results;
        state.initialMyList = results;
      } catch {
        ElNotification({ title: 'Erreur', message: 'Erreur lors de la désactivation du tenant.', type: 'error' });
      }
    };

    return {
      state,
      tableHeader,
      tableFilters,
      displayTenant,
      onDeactivate,
    };
  },
});
</script>
